/**
 * React Aria's two root providers, lifted out of `App.tsx` so a test can wear them too.
 *
 * They were inline in `App.tsx`, which meant the app's provider tree and the test harness's
 * provider tree were different trees — and the difference was exactly the thing `i-0004`
 * turned on. A RAC `Link` only routes through the app's router when `RouterProvider` is
 * above it; without one it navigates the document. So a suite that omitted this provider
 * could not tell a client-side navigation from a full page load, and a card that reloaded
 * the whole Studio on every click looked green.
 *
 * `navigate` is a prop rather than a module-scope router, because the app has a browser
 * router and a test has a memory one, and creating the app's router just to render a test
 * would put a second router on the page.
 */

import {
  I18nProvider as AriaI18nProvider,
  RouterProvider as AriaRouterProvider,
} from 'react-aria-components';
import type { JSX, ReactNode } from 'react';
import type { RouterNavigateOptions } from 'react-router';

import { useI18n } from './i18n/I18nProvider.js';

export interface AriaProvidersProps {
  /** The router's own `navigate` — a browser router's in the app, a memory one's in a test. */
  navigate: (to: string, options?: RouterNavigateOptions) => void | Promise<void>;
  children: ReactNode;
}

export function AriaProviders({ navigate, children }: AriaProvidersProps): JSX.Element {
  const { locale } = useI18n();
  return (
    <AriaI18nProvider locale={locale}>
      {/* The lambda stays here so RAC infers its own option type at the call site. */}
      <AriaRouterProvider navigate={(to, options) => void navigate(to, options)}>
        {children}
      </AriaRouterProvider>
    </AriaI18nProvider>
  );
}
